import React, { useState, useEffect, useRef } from "react";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { Chart } from "primereact/chart";
import { Dropdown } from "primereact/dropdown";
import reportService from "../services/reportService";
import { hideProcessing, showProcessing } from "../utils/utils";
import "./styles/relatorio.css";
import InputPeriodo from "./components/inputPeriodo";

let _relatorioInitialized = false;

export default function Relatorio() {
    const toast = useRef(null);
    const [tipoFiltro, setTipoFiltro] = useState('nao_finalizadas');
    const [inicio, setInicio] = useState(new Date(new Date().setDate(new Date().getDate() - 30)));
    const [fim, setFim] = useState(new Date());
    const [relatorios, setRelatorios] = useState([]);
    const [indicadores, setIndicadores] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!_relatorioInitialized) {
            _relatorioInitialized = true;
            return;
        }
        
        buscarRelatorio();
    }, []);

    const buscarRelatorio = async () => {
        showProcessing();
        setLoading(true);
        try {
            let dataInicio = null;
            let dataFim = null;

            if (tipoFiltro === 'por_periodo') {
                dataInicio = inicio.toISOString().split('T')[0];
                dataFim = fim.toISOString().split('T')[0];
            }

            const [responseRelatorio, responseIndicadores] = await Promise.all([
                reportService.getRelatorioOrdems(dataInicio, dataFim),
                reportService.getIndicadores(dataInicio, dataFim)
            ]);

            setRelatorios(responseRelatorio.data || []);
            setIndicadores(responseIndicadores.data || {});

        } catch (error) {
            toast.current?.show({
                severity: "error",
                summary: "Erro",
                detail: "Erro ao carregar relatório"
            });
        } finally {
            setLoading(false);
            hideProcessing();
        }
    };

    // Templates para formatação
    const statusBodyTemplate = (rowData) => {
        const statusMap = {
            'FINALIZADA': 'success',
            'CANCELADA': 'danger',
            'PAGAMENTO CONFIRMADO': 'info',
            'ORÇAMENTO': 'warning',
            'PARECER DE ACESSO': 'warning'
        };
        
        return <Tag value={rowData.status} severity={statusMap[rowData.status] || 'secondary'} />;
    };

    const tipoBodyTemplate = (rowData) => {
        const severidade = rowData.tipo === 'FOTOVOLTAICA' ? 'info' : 'secondary';
        return <Tag value={rowData.tipo} severity={severidade} />;
    };

    const moedaBodyTemplate = (rowData, column) => {
        const valor = rowData[column.field];
        return reportService.formatarMoeda(valor);
    };

    const percentualBodyTemplate = (rowData, column) => {
        const valor = rowData[column.field];
        return reportService.formatarPercentual(valor);
    };

    const dataBodyTemplate = (rowData, column) => {
        const data = rowData[column.field];
        return reportService.formatarData(data);
    };

    const alertaBodyTemplate = (rowData) => {
        const alertas = [];
        
        if (rowData.orcamentoAtrasado) {
            alertas.push(
                <Tag key="orcamento" value="Orçamento Atrasado" severity="danger" style={{ marginRight: "5px" }} />
            );
        }
        
        if (rowData.pagamentoAtrasado) {
            alertas.push(
                <Tag key="pagamento" value="Pagamento Atrasado" severity="danger" style={{ marginRight: "5px" }} />
            );
        }
        
        return alertas.length > 0 ? alertas : <Tag value="OK" severity="success" />;
    };

    // Gráficos
    const gerarGraficoIndicadores = () => {
        if (!indicadores || !indicadores.topVendedores) return null;

        const vendedores = indicadores.topVendedores || [];
        return {
            labels: vendedores.map(v => v.nomeVendedor),
            datasets: [
                {
                    label: 'Valor Total Bruto',
                    data: vendedores.map(v => v.valorTotalBruto),
                    backgroundColor: 'rgba(54, 162, 235, 0.5)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1
                },
                {
                    label: 'Valor Total Lucro',
                    data: vendedores.map(v => v.valorTotalLucro),
                    backgroundColor: 'rgba(75, 192, 75, 0.5)',
                    borderColor: 'rgba(75, 192, 75, 1)',
                    borderWidth: 1
                }
            ]
        };
    };

    const gerarGraficoTipo = () => {
        if (!indicadores || !indicadores.porTipoVenda) return null;

        const tipos = indicadores.porTipoVenda || [];
        return {
            labels: tipos.map(t => t.tipo),
            datasets: [{
                label: 'Quantidade',
                data: tipos.map(t => t.quantidade),
                backgroundColor: [
                    'rgba(255, 99, 132, 0.5)',
                    'rgba(54, 162, 235, 0.5)',
                    'rgba(255, 206, 86, 0.5)',
                    'rgba(75, 192, 192, 0.5)',
                    'rgba(153, 102, 255, 0.5)'
                ],
                borderColor: [
                    'rgba(255, 99, 132, 1)',
                    'rgba(54, 162, 235, 1)',
                    'rgba(255, 206, 86, 1)',
                    'rgba(75, 192, 192, 1)',
                    'rgba(153, 102, 255, 1)'
                ],
                borderWidth: 1
            }]
        };
    };

    const gerarGraficoStatus = () => {
        if (!indicadores || !indicadores.porStatus) return null;

        const status = indicadores.porStatus || [];
        return {
            labels: status.map(s => s.status),
            datasets: [{
                label: 'Quantidade por Status',
                data: status.map(s => s.quantidade),
                backgroundColor: [
                    'rgba(255, 99, 132, 0.5)',
                    'rgba(54, 162, 235, 0.5)',
                    'rgba(255, 206, 86, 0.5)',
                    'rgba(75, 192, 192, 0.5)',
                    'rgba(153, 102, 255, 0.5)',
                    'rgba(255, 159, 64, 0.5)',
                    'rgba(201, 203, 207, 0.5)',
                    'rgba(255, 99, 132, 0.5)',
                    'rgba(54, 162, 235, 0.5)',
                    'rgba(255, 206, 86, 0.5)',
                    'rgba(75, 192, 192, 0.5)',
                    'rgba(153, 102, 255, 0.5)'
                ],
                borderColor: [
                    'rgba(255, 99, 132, 1)',
                    'rgba(54, 162, 235, 1)',
                    'rgba(255, 206, 86, 1)',
                    'rgba(75, 192, 192, 1)',
                    'rgba(153, 102, 255, 1)',
                    'rgba(255, 159, 64, 1)',
                    'rgba(201, 203, 207, 1)',
                    'rgba(255, 99, 132, 1)',
                    'rgba(54, 162, 235, 1)',
                    'rgba(255, 206, 86, 1)',
                    'rgba(75, 192, 192, 1)',
                    'rgba(153, 102, 255, 1)'
                ],
                borderWidth: 1
            }]
        };
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
            legend: {
                position: 'top'
            }
        }
    };

    return (
        <div className="relatorio-container">
            <Toast ref={toast} />
            
            {/* Header */}
            <Card className="header-card">
                <div className="filtros-relatorio">
                    <div className="filtro-item">
                        <label style={{ display: "block", marginBottom: "5px" }}>Filtrar por</label>
                        <Dropdown
                            value={tipoFiltro}
                            onChange={(e) => setTipoFiltro(e.value)}
                            options={[
                                { label: 'Não Finalizadas', value: 'nao_finalizadas' },
                                { label: 'Por Período', value: 'por_periodo' }
                            ]}
                            style={{ minWidth: '150px' }}
                        />
                    </div>

                    {tipoFiltro === 'por_periodo' && (
                        <div className="filtro-item">
                            <label style={{ display: "block", marginBottom: "5px" }}>Período</label>
                            <InputPeriodo
                                inicio={inicio}
                                fim={fim}
                                onChangePeriodo={(e) => {
                                    setInicio(e.inicio);
                                    setFim(e.fim);
                                }}
                            />
                        </div>
                    )}

                    <Button 
                        label="Buscar" 
                        icon="pi pi-search" 
                        onClick={buscarRelatorio}
                        loading={loading}
                        className="button-confirm"
                    />
                </div>
            </Card>

            {/* Indicadores Principais */}
            <div className="indicadores-grid">
                <Card className="indicador-card">
                    <div className="indicador-content">
                        <div className="indicador-label">Ordens Abertos</div>
                        <div className="indicador-valor">{indicadores?.totalOrdensAbertos || 0}</div>
                    </div>
                </Card>

                <Card className="indicador-card">
                    <div className="indicador-content">
                        <div className="indicador-label">Ordens Finalizados</div>
                        <div className="indicador-valor">{indicadores?.totalOrdensFinalizados || 0}</div>
                    </div>
                </Card>

                <Card className="indicador-card alert-card">
                    <div className="indicador-content">
                        <div className="indicador-label">Ordens Atrasados</div>
                        <div className="indicador-valor" style={{ color: '#d32f2f' }}>
                            {indicadores?.totalOrdensAtrasados || 0}
                        </div>
                    </div>
                </Card>

                <Card className="indicador-card">
                    <div className="indicador-content">
                        <div className="indicador-label">Valor Total Bruto</div>
                        <div className="indicador-valor">
                            {reportService.formatarMoeda(indicadores?.valorTotalBruto || 0)}
                        </div>
                    </div>
                </Card>

                <Card className="indicador-card">
                    <div className="indicador-content">
                        <div className="indicador-label">Valor Total Líquido</div>
                        <div className="indicador-valor">
                            {reportService.formatarMoeda(indicadores?.valorTotalLiquido || 0)}
                        </div>
                    </div>
                </Card>

                <Card className="indicador-card">
                    <div className="indicador-content">
                        <div className="indicador-label">Lucro Total</div>
                        <div className="indicador-valor" style={{ color: '#2e7d32' }}>
                            {reportService.formatarMoeda(indicadores?.valorTotalLucro || 0)}
                        </div>
                    </div>
                </Card>

                <Card className="indicador-card">
                    <div className="indicador-content">
                        <div className="indicador-label">Margem de Lucro</div>
                        <div className="indicador-valor">
                            {reportService.formatarPercentual(indicadores?.margemLucroMedia || 0)}
                        </div>
                    </div>
                </Card>

                <Card className="indicador-card">
                    <div className="indicador-content">
                        <div className="indicador-label">Total Descontos</div>
                        <div className="indicador-valor">
                            {reportService.formatarMoeda(indicadores?.valorTotalDesconto || 0)}
                        </div>
                    </div>
                </Card>
            </div>

            {/* Indicadores Fotovoltaica */}
            <Card className="section-card">
                <h3 style={{ marginTop: 0 }}>Indicadores - Fotovoltaica</h3>
                <div className="indicadores-grid" style={{ marginTop: "20px" }}>
                    <Card className="indicador-card">
                        <div className="indicador-content">
                            <div className="indicador-label">Pendentes (Parecer)</div>
                            <div className="indicador-valor">
                                {indicadores?.totalFotovoltaicaPendente || 0}
                            </div>
                        </div>
                    </Card>

                    <Card className="indicador-card">
                        <div className="indicador-content">
                            <div className="indicador-label">Com Parecer Aprovado</div>
                            <div className="indicador-valor">
                                {indicadores?.totalFotovoltaicaPareceAprovado || 0}
                            </div>
                        </div>
                    </Card>

                    <Card className="indicador-card alert-card">
                        <div className="indicador-content">
                            <div className="indicador-label">Orçamento Atrasado</div>
                            <div className="indicador-valor" style={{ color: '#d32f2f' }}>
                                {indicadores?.totalFotovoltaicaOrcamentoAtrasado || 0}
                            </div>
                        </div>
                    </Card>

                    <Card className="indicador-card alert-card">
                        <div className="indicador-content">
                            <div className="indicador-label">Pagamento Atrasado</div>
                            <div className="indicador-valor" style={{ color: '#d32f2f' }}>
                                {indicadores?.totalFotovoltaicaPagamentoAtrasado || 0}
                            </div>
                        </div>
                    </Card>
                </div>
            </Card>

            {/* Gráficos */}
            <Card className="section-card">
                <h3 style={{ marginTop: 0 }}>Top 5 Vendedores</h3>
                {gerarGraficoIndicadores() && (
                    <Chart type="bar" data={gerarGraficoIndicadores()} options={chartOptions} />
                )}
            </Card>

            <div className="chart-row">
                <Card className="section-card" style={{ flex: 1 }}>
                    <h3 style={{ marginTop: 0 }}>Vendas por Tipo</h3>
                    {gerarGraficoTipo() && (
                        <Chart type="doughnut" data={gerarGraficoTipo()} options={chartOptions} />
                    )}
                </Card>

                <Card className="section-card" style={{ flex: 1 }}>
                    <h3 style={{ marginTop: 0 }}>Ordens por Status</h3>
                    {gerarGraficoStatus() && (
                        <Chart type="bar" data={gerarGraficoStatus()} options={chartOptions} />
                    )}
                </Card>
            </div>

            {/* Tabela de Detalhes */}
            <Card className="section-card">
                <h3 style={{ marginTop: 0 }}>Detalhes de Ordens</h3>
                <DataTable
                    value={relatorios}
                    loading={loading}
                    dataKey="id"
                    className="datatable-responsive"
                    responsiveLayout="scroll"
                    paginator
                    rows={10}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords}"
                >
                    <Column field="id" header="ID" sortable style={{ width: "80px" }} />
                    <Column field="nomeVendedor" header="Vendedor" sortable />
                    <Column 
                        field="tipo" 
                        header="Tipo" 
                        body={tipoBodyTemplate}
                        sortable 
                        style={{ width: "120px" }}
                    />
                    <Column 
                        field="status" 
                        header="Status" 
                        body={statusBodyTemplate}
                        sortable 
                        style={{ width: "150px" }}
                    />
                    <Column 
                        field="criadoEm" 
                        header="Data Criação" 
                        body={(rowData) => dataBodyTemplate(rowData, { field: 'criadoEm' })}
                        sortable
                        style={{ width: "120px" }}
                    />
                    <Column 
                        field="valorBruto" 
                        header="Valor Bruto" 
                        body={(rowData) => moedaBodyTemplate(rowData, { field: 'valorBruto' })}
                        sortable
                        style={{ width: "120px", textAlign: "right" }}
                    />
                    <Column 
                        field="desconto" 
                        header="Desconto" 
                        body={(rowData) => moedaBodyTemplate(rowData, { field: 'desconto' })}
                        style={{ width: "100px", textAlign: "right" }}
                    />
                    <Column 
                        field="valorLiquido" 
                        header="Valor Líquido" 
                        body={(rowData) => moedaBodyTemplate(rowData, { field: 'valorLiquido' })}
                        sortable
                        style={{ width: "120px", textAlign: "right" }}
                    />
                    <Column 
                        field="custo" 
                        header="Custo" 
                        body={(rowData) => moedaBodyTemplate(rowData, { field: 'custo' })}
                        style={{ width: "100px", textAlign: "right" }}
                    />
                    <Column 
                        field="lucro" 
                        header="Lucro" 
                        body={(rowData) => moedaBodyTemplate(rowData, { field: 'lucro' })}
                        sortable
                        style={{ width: "100px", textAlign: "right" }}
                    />
                    <Column 
                        field="margemLucro" 
                        header="Margem %" 
                        body={(rowData) => percentualBodyTemplate(rowData, { field: 'margemLucro' })}
                        sortable
                        style={{ width: "100px", textAlign: "right" }}
                    />
                    {/* Colunas específicas para Fotovoltaica */}
                    <Column 
                        field="statusParecerDeAcesso" 
                        header="Status Parecer" 
                        style={{ width: "130px" }}
                    />
                    <Column 
                        field="dataParecerDeAcesso" 
                        header="Data Parecer" 
                        body={(rowData) => dataBodyTemplate(rowData, { field: 'dataParecerDeAcesso' })}
                        style={{ width: "120px" }}
                    />
                    <Column 
                        field="dataFinalizacaoOrcamento" 
                        header="Data Finaliz. Orçamento" 
                        body={(rowData) => dataBodyTemplate(rowData, { field: 'dataFinalizacaoOrcamento' })}
                        style={{ width: "150px" }}
                    />
                    <Column 
                        field="diasParaFinalizarOrcamento" 
                        header="Dias p/ Finalizar" 
                        style={{ width: "130px" }}
                    />
                    <Column 
                        field="dataConfirmacaoPagamento" 
                        header="Data Pagamento" 
                        body={(rowData) => dataBodyTemplate(rowData, { field: 'dataConfirmacaoPagamento' })}
                        style={{ width: "120px" }}
                    />
                    <Column 
                        field="diasParaConfirmarPagamento" 
                        header="Dias p/ Pagamento" 
                        style={{ width: "130px" }}
                    />
                    <Column 
                        body={alertaBodyTemplate}
                        header="Alertas"
                        style={{ width: "200px" }}
                    />
                    <Column 
                        field="proximaVisita" 
                        header="Próxima Visita" 
                        body={(rowData) => dataBodyTemplate(rowData, { field: 'proximaVisita' })}
                        style={{ width: "120px" }}
                    />
                    <Column 
                        field="ultimaVisita" 
                        header="Última Visita" 
                        body={(rowData) => dataBodyTemplate(rowData, { field: 'ultimaVisita' })}
                        style={{ width: "120px" }}
                    />
                </DataTable>
            </Card>
        </div>
    );
}
